/**
 * Notification Hooks
 *
 * TanStack Query hooks for in-app notifications.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { UserNotification, ParsedNotification } from '@/shared/schemas/notification.schema'

const API_BASE = '/api/notifications'

// Query keys factory
const NOTIFICATION_KEYS = {
  all: ['notifications'] as const,
  lists: () => [...NOTIFICATION_KEYS.all, 'list'] as const,
  list: (params?: { unreadOnly?: boolean }) =>
    [...NOTIFICATION_KEYS.lists(), params] as const,
  unreadCount: () => [...NOTIFICATION_KEYS.all, 'unread-count'] as const,
}

interface NotificationListResponse {
  notifications: UserNotification[]
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}

interface UnreadCountResponse {
  count: number
}

/**
 * Parse notification data JSON
 */
function parseNotification(notification: UserNotification): ParsedNotification {
  let parsedData = undefined
  if (notification.data) {
    try {
      parsedData = JSON.parse(notification.data)
    } catch {
      // Invalid JSON, ignore
    }
  }
  return {
    ...notification,
    data: parsedData,
  }
}

/**
 * Fetch notifications list
 */
export function useNotifications(options?: { unreadOnly?: boolean; limit?: number }) {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.list({ unreadOnly: options?.unreadOnly }),
    queryFn: async (): Promise<ParsedNotification[]> => {
      const params = new URLSearchParams()
      if (options?.unreadOnly) {
        params.set('unreadOnly', 'true')
      }
      if (options?.limit) {
        params.set('limit', String(options.limit))
      }

      const url = params.toString() ? `${API_BASE}?${params}` : API_BASE
      const response = await fetch(url, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch notifications')
      }

      const data: NotificationListResponse = await response.json()
      return data.notifications.map(parseNotification)
    },
    // Refetch every 30 seconds for near-real-time updates
    refetchInterval: 30000,
  })
}

/**
 * Fetch unread notification count for badge
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: NOTIFICATION_KEYS.unreadCount(),
    queryFn: async (): Promise<number> => {
      const response = await fetch(`${API_BASE}/unread-count`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to fetch unread count')
      }

      const data: UnreadCountResponse = await response.json()
      return data.count
    },
    // Refetch every 30 seconds for near-real-time updates
    refetchInterval: 30000,
  })
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      read = true,
    }: {
      id: string
      read?: boolean
    }): Promise<UserNotification> => {
      const response = await fetch(`${API_BASE}/${id}/read`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ read }),
      })

      if (!response.ok) {
        throw new Error('Failed to mark notification as read')
      }

      const data: { notification: UserNotification } = await response.json()
      return data.notification
    },
    onSuccess: () => {
      // Invalidate both list and count queries
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount() })
    },
  })
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<{ updated: number }> => {
      const response = await fetch(`${API_BASE}/read-all`, {
        method: 'PATCH',
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to mark all notifications as read')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate both list and count queries
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount() })
    },
  })
}

/**
 * Delete a notification
 */
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await fetch(`${API_BASE}/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to delete notification')
      }
    },
    onSuccess: () => {
      // Invalidate both list and count queries
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount() })
    },
  })
}

/**
 * Clear all notifications
 */
export function useClearAllNotifications() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => {
      const response = await fetch(API_BASE, {
        method: 'DELETE',
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error('Failed to clear notifications')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate both list and count queries
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.lists() })
      queryClient.invalidateQueries({ queryKey: NOTIFICATION_KEYS.unreadCount() })
    },
  })
}
